import PageNavbar from '../../components/feature/PageNavbar';
import { useNavigate } from 'react-router-dom';

export default function TechnologyPage() {
  const navigate = useNavigate();
  
  const features = [
    {
      title: 'Enhanced Recovery',
      description: 'Precise, targeted therapy delivers measurable improvements'
    },
    {
      title: 'Real-time Tracking',
      description: 'Continuous progress monitoring with adaptive programs'
    },
    {
      title: 'Increased Engagement',
      description: 'Gamified experiences make rehabilitation enjoyable'
    }
  ];

  const technologies = [
    {
      title: 'Robotics',
      description: 'Advanced robotic systems for precise movement therapy and consistent rehabilitation outcomes.'
    },
    {
      title: 'VR Systems',
      description: 'Immersive environments for engaging therapy sessions that make recovery enjoyable.'
    },
    {
      title: 'Motion Capture',
      description: 'Precise movement analysis and tracking for optimized rehabilitation programs.'
    },
    {
      title: 'Gamification',
      description: 'Engaging exercises for better outcomes through game-based rehabilitation.'
    }
  ];

  const equipment = [
    {
      name: 'AMADEO',
      description: 'Advanced hand rehabilitation robot for fine motor recovery'
    },
    {
      name: 'DIEGO',
      description: 'Upper extremity rehabilitation system for arm and shoulder therapy'
    },
    {
      name: 'PABLO',
      description: 'Multi-joint arm training system with real-time feedback'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <PageNavbar />

      {/* Innovation Meets Recovery Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-medium">
              Advanced Recovery
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Innovation Meets Recovery
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Where cutting-edge technology accelerates your rehabilitation journey
          </p>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <div className="mb-8">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our state-of-the-art recovery centre features a <span className="text-blue-600 font-semibold underline decoration-blue-200 decoration-2">therapeutic environment</span> designed to promote engagement, comfort, and safety. It is equipped with robotic-assisted gait trainers, arm and hand exoskeletons, immersive VR systems, motion capture tools, and Tyromotion neurorehabilitation technology.
                </p>
              </div>

              {/* Features */}
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 font-bold text-sm shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="flex flex-col items-center">
              <div className="rounded-2xl overflow-hidden shadow-xl w-full">
                <img
                  src="https://static.readdy.ai/image/c1d3a3065b1a0920ec31881e87c350dd/1990a7c36ce0e0a71db1c0c0cb98094f.jpeg"
                  alt="Advanced Recovery Technology"
                  className="w-full h-[400px] object-cover object-center"
                />
              </div>
            </div>
          </div>

          {/* CTA Button - Centered at bottom of section */}
          <div className="flex justify-center mt-12">
            <button 
              onClick={() => navigate('/equipment')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore Our Equipment
            </button>
          </div>
        </div>
      </section>

      {/* Technology Integration Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-medium">
              Integration
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Technology Integration
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Four pillars of innovation working together to accelerate your recovery
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600 font-bold text-sm mb-4">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{tech.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 rounded-full text-sm font-medium">
              Our Equipment
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Tyromotion Systems
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            World-leading rehabilitation technology for optimal recovery outcomes
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {equipment.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full mx-auto mb-4 shadow-sm text-blue-600 font-bold">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.name}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Experience the Future of Rehabilitation
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our advanced technology combined with expert care creates the optimal environment for your recovery journey.
          </p>
          <a
            href="mailto:enquire@archealth.net.au"
            className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line text-lg"></i>
            <span>Contact Us</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 py-8 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
